import React from "react"
import PropTypes from "prop-types"
import { NeoButton, NeoInput, NeoBadge, NeoCard, NeoAlert } from "../../../components/Common"
import { useTestFormLogic } from "../Hooks/useTestFormLogic"
import InputMask from "react-input-mask"

const TestEvaluationForm = ({
    idActivity,
    evaluationLogic,
}) => {
    const {
        isLoading,
        withLoading,
        formReady,
        activeTestEvent,
        allMembers,
        evaluationMembers,
        toggleExcludeMember,
        excludedMemberIds
    } = evaluationLogic

    const {
        testDrafts,
        handleResultChange,
        saveTests,
        dirtyCount: testsDirtyCount
    } = useTestFormLogic({
        idActivity,
        activeEvent: activeTestEvent,
        members: allMembers,
        excludedIds: excludedMemberIds,
        withLoading
    })

    // ─── LOADING GATE ───────────────────────────────────────────────
    if (!formReady) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center py-5 animate__animated animate__fadeIn" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <i className="mdi mdi-loading mdi-spin text-primary font-size-32 mb-3" />
                <p className="text-muted mb-0 font-size-14">Preparando teste...</p>
            </div>
        )
    }

    if (!activeTestEvent) {
        return (
            <NeoAlert
                color="info"
                title="Ciclo de Testes indisponível"
            >
                Não há provas ou testes de performance agendados para este período.
            </NeoAlert>
        )
    }
    // ─── FIM DO LOADING GATE ─────────────────────────────────────────

    const safeRender = (val) => {
        if (!val) return '';
        if (Array.isArray(val)) return val.map(v => typeof v === 'object' ? (v.label || v.description || v.id || '') : v).join(', ');
        if (typeof val === 'object') return val.label || val.name || val.description || val.value || val.id || '[Objeto]';
        return String(val);
    };

    return (
        <div style={{ fontFamily: 'Outfit, sans-serif' }}>
            {/* Active Test Event Header Card (Thin Card) */}
            <NeoCard
                thinBorder
                className="mb-4 bg-white animate__animated animate__fadeIn animate__faster"
                bodyClassName="p-3"
            >
                <div className="d-flex align-items-center gap-3">
                    <div className="flex-shrink-0 bg-white rounded-circle border shadow-sm d-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
                        <i className={`mdi ${(['fixed_time', 'distance'].includes(activeTestEvent?.testType)) ? 'mdi-timer-sand' : 'mdi-run-fast'} fs-3 text-primary`}></i>
                    </div>
                    <div>
                        <h6 className="mb-1 fw-bold text-dark">{activeTestEvent.name}</h6>
                        <div className="small text-muted">
                            <strong>Prova: </strong>
                            {(['fixed_time', 'distance'].includes(activeTestEvent?.testType))
                                ? `Tempo Fixo (${activeTestEvent?.testConfig?.referenceValue || 0}${activeTestEvent?.testConfig?.unit || ''}) - Medir Distância`
                                : `Distância Fixa (${activeTestEvent?.testConfig?.referenceValue || 0}${activeTestEvent?.testConfig?.unit || ''}) - Medir Tempo`
                            }
                        </div>
                    </div>
                </div>
            </NeoCard>

            {/* Listagem de Alunos em Card Robustizado */}
            <NeoCard
                thinBorder
                className="bg-white shadow-sm"
                bodyClassName="p-4"
                title="Alunos para Teste"
                subtitle="Insira e salve o resultado de performance obtido por cada aluno"
                headerIcon="mdi-run-fast"
                headerActions={<NeoBadge color="secondary" className="px-3 py-1 font-size-12">{evaluationMembers.length}</NeoBadge>}
            >
                {evaluationMembers.length === 0 ? (
                    <div className="text-center py-5 border rounded bg-light border-dashed">
                        <i className="mdi mdi-account-search-outline fs-1 text-muted opacity-50 mb-3 d-block"></i>
                        <p className="text-muted mb-0 font-size-13">Nenhum aluno apto encontrado.</p>
                    </div>
                ) : (
                    <div className="d-flex flex-column gap-3">
                        {evaluationMembers.map(member => {
                            const sId = String(member.id)
                            const testDraft = testDrafts[sId] || { result: '', notes: '' }

                            return (
                                <div key={sId} className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3 p-3 bg-white rounded border shadow-sm mb-2">
                                    <div className="d-flex align-items-center gap-3 w-100 w-md-auto">
                                        <div className="flex-shrink-0">
                                            {member.photo ? (
                                                <img
                                                    src={member.photo}
                                                    alt={member.name}
                                                    className="rounded-circle border"
                                                    style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <div className="rounded-circle border bg-light d-flex align-items-center justify-content-center shadow-sm" style={{ width: '48px', height: '48px' }}>
                                                    <i className="mdi mdi-account fs-3" style={{ color: '#9e9e9e' }}></i>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex-grow-1">
                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                                <div className="fw-bold text-dark fs-5">{safeRender(member.name)}</div>
                                                {member.memberNumber && <span className="text-muted small">#{safeRender(member.memberNumber)}</span>}
                                            </div>
                                            <div className="d-flex gap-2 align-items-center mt-1">
                                                {member.status && (
                                                    <NeoBadge color={
                                                        member.status === 'active' ? 'success' :
                                                            member.status === 'suspended' ? 'warning' : 'secondary'
                                                    } className="border px-2 text-capitalize">
                                                        {member.status === 'active' ? 'Ativo' : safeRender(member.status)}
                                                    </NeoBadge>
                                                )}
                                                {member.tag && <span className="badge bg-light text-muted border d-none d-md-inline-block font-size-10">{safeRender(member.tag)}</span>}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="d-flex flex-column flex-md-row align-items-center gap-2 gap-md-3 w-100 w-md-auto ms-auto">
                                        <div className="w-100 w-md-auto" style={{ minWidth: '160px' }}>
                                            <div className="d-md-none small text-muted text-uppercase fw-bold mb-1 text-center">
                                                Inserir Resultado
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                {['fixed_time', 'distance'].includes(activeTestEvent?.testType) ? (
                                                    <NeoInput
                                                        type="number"
                                                        placeholder="Metros"
                                                        flat={true}
                                                        size="sm"
                                                        style={{ minWidth: '150px' }}
                                                        inputStyle={{ textAlign: 'center', fontWeight: 'bold' }}
                                                        value={testDraft.result}
                                                        onChange={(e) => handleResultChange(sId, e.target.value)}
                                                        disabled={!activeTestEvent}
                                                    />
                                                ) : (
                                                    <InputMask
                                                        mask="99:99:99:99"
                                                        value={testDraft.result}
                                                        onChange={(e) => handleResultChange(sId, e.target.value)}
                                                        disabled={!activeTestEvent}
                                                    >
                                                        {(inputProps) => (
                                                            <NeoInput
                                                                {...inputProps}
                                                                placeholder="HH:MM:SS:CC"
                                                                flat={true}
                                                                size="sm"
                                                                style={{ minWidth: '150px' }}
                                                                inputStyle={{ textAlign: 'center', fontWeight: 'bold' }}
                                                            />
                                                        )}
                                                    </InputMask>
                                                )}
                                            </div>
                                        </div>

                                        <div className="d-flex align-items-center gap-2 w-100 w-md-auto justify-content-center">
                                            <NeoButton
                                                variant="soft-danger"
                                                onClick={() => toggleExcludeMember(sId)}
                                                title="Remover da lista"
                                                icon="mdi-close-circle-outline"
                                                className="flex-grow-1 flex-md-grow-0"
                                                size="sm"
                                            >
                                                <span className="ms-1 d-md-none">Remover</span>
                                            </NeoButton>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}

                {excludedMemberIds.size > 0 && (
                    <div className="mt-4 animate__animated animate__fadeIn">
                        <div className="d-flex align-items-center justify-content-between mb-2 px-1">
                            <h6 className="small fw-bold text-muted text-uppercase mb-0">
                                <i className="mdi mdi-eye-off-outline me-1"></i>
                                Alunos Ocultos ({excludedMemberIds.size})
                            </h6>
                            <button
                                type="button"
                                className="btn btn-link p-0 text-decoration-none small text-primary fw-bold"
                                onClick={() => {
                                    excludedMemberIds.forEach(id => toggleExcludeMember(id))
                                }}
                            >
                                Restaurar Todos
                            </button>
                        </div>

                        <div className="d-flex flex-column gap-2 bg-soft-light p-2 rounded border border-dashed">
                            {allMembers
                                .filter(c => excludedMemberIds.has(String(c.id)))
                                .map(member => (
                                    <div key={member.id} className="d-flex align-items-center justify-content-between p-2 bg-white rounded border-bottom shadow-sm" style={{ opacity: 0.8 }}>
                                        <div className="d-flex align-items-center gap-2">
                                            {member.photo ? (
                                                <img
                                                    src={member.photo}
                                                    alt={member.name}
                                                    className="rounded-circle border"
                                                    style={{ width: '24px', height: '24px', objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <div className="rounded-circle border bg-light d-flex align-items-center justify-content-center shadow-sm" style={{ width: '24px', height: '24px' }}>
                                                    <i className="mdi mdi-account" style={{ fontSize: '14px', color: '#9e9e9e' }}></i>
                                                </div>
                                            )}
                                            <span className="small fw-medium text-muted">{member.name}</span>
                                        </div>
                                        <NeoButton
                                            variant="outline-primary"
                                            size="sm"
                                            style={{ height: '24px', fontSize: '10px', padding: '0 8px' }}
                                            onClick={() => toggleExcludeMember(member.id)}
                                        >
                                            Restaurar
                                        </NeoButton>
                                    </div>
                                ))}
                        </div>
                    </div>
                )}

                <div className="d-flex justify-content-end mt-4 pt-3 border-top gap-2">
                    <NeoButton
                        variant="primary"
                        onClick={saveTests}
                        loading={isLoading("saveTests")}
                        disabled={isLoading("saveTests") || testsDirtyCount === 0 || !activeTestEvent}
                        className="px-4 shadow-sm"
                    >
                        Salvar
                    </NeoButton>
                </div>
            </NeoCard>
        </div>
    )
}

TestEvaluationForm.propTypes = {
    idActivity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    evaluationLogic: PropTypes.object
}

export default TestEvaluationForm
